import { ClaimMembershipData } from './claim-membership-data.model';
import { MembershipRoles } from './membership-roles.model';
import { MembershipType } from './membership-type.model';

/** Cached numeric values of MembershipRoles for iteration without per-call allocation */
const MEMBERSHIP_ROLE_VALUES = Object.values(MembershipRoles).filter(
  (v): v is MembershipRoles => typeof v === 'number'
);

/**
 * Organization membership with bitwise role permissions and membership type
 */
export class ClaimMembership {
  organizationId: string;
  claims: number;
  membershipType: MembershipType;

  constructor(organizationId: string, claims: number = 0, membershipType: MembershipType = MembershipType.Provider) {
    this.organizationId = organizationId;
    this.claims = claims;
    this.membershipType = membershipType;
  }

  hasRole(role: MembershipRoles): boolean {
    return (this.claims & role) === role;
  }

  hasAnyRole(roles: MembershipRoles[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(roles: MembershipRoles[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  getRoles(): MembershipRoles[] {
    const roles: MembershipRoles[] = [];
    for (const role of MEMBERSHIP_ROLE_VALUES) {
      if (this.hasRole(role)) roles.push(role);
    }
    return roles;
  }

  setRole(role: MembershipRoles): void {
    this.claims |= role;
  }

  removeRole(role: MembershipRoles): void {
    this.claims &= ~role;
  }

  toJSON(): ClaimMembershipData {
    const data: ClaimMembershipData = { o: this.organizationId, c: this.claims };
    if (this.membershipType !== MembershipType.Provider) {
      data.t = this.membershipType;
    }
    return data;
  }

  static fromJSON(json: ClaimMembershipData): ClaimMembership {
    return new ClaimMembership(
      json.o,
      json.c,
      (json.t as MembershipType) || MembershipType.Provider
    );
  }
}
